import React from 'react';
import { IconCircleCheck, IconRefresh, IconChevronDown } from '@tabler/icons-react';
import type { AvatarColour } from '../../styles/avatarPalette';
import './TechnicianCard.css';
import './rowsNarrow.css';

interface TechnicianCardProps {
  /** Avatar initials, e.g. "ST". */
  initials: string;
  /** Resolved avatar colour (locked palette). */
  avatarColor?: AvatarColour;
  name: string;
  /** Work-item count shown beside the name as "(20)". */
  count: number;
  /** JobRow / AppointmentRow children. */
  children?: React.ReactNode;
  /** Header action icons — all optional and presentational. */
  onResolvedToday?: () => void;
  onRefresh?: () => void;
  onToggle?: () => void;
  /**
   * Collapsed state. The jobs list stays mounted (hidden via display:none) so
   * any SortableContext inside remains a valid drop target while collapsed.
   */
  collapsed?: boolean;
}

/**
 * Technician grouping card: a header (avatar, name, count, action icons) over a
 * stacked list of JobRow / AppointmentRow children. Purely presentational.
 */
export const TechnicianCard: React.FC<TechnicianCardProps> = ({
  initials,
  avatarColor,
  name,
  count,
  children,
  onResolvedToday,
  onRefresh,
  onToggle,
  collapsed = false,
}) => (
  <div className="tech-card">
    <div className="tech-card__header">
      <div
        className="tech-card__avatar"
        style={avatarColor ? { background: avatarColor.bg, color: avatarColor.fg } : undefined}
      >
        {initials}
      </div>
      <span className="tech-card__name">{name}</span>
      <span className="tech-card__count">({count})</span>
      <div className="tech-card__spacer" />
      <IconCircleCheck className="tech-card__action-icon" size={20} stroke={1.5} onClick={onResolvedToday} />
      <IconRefresh className="tech-card__action-icon" size={20} stroke={1.5} onClick={onRefresh} />
      <IconChevronDown
        className="tech-card__action-icon tech-card__action-icon--chevron"
        size={20}
        stroke={1.5}
        onClick={onToggle}
        style={{ transform: collapsed ? 'rotate(-90deg)' : undefined, transition: 'transform 0.2s ease' }}
      />
    </div>
    <div className="tech-card__jobs" style={collapsed ? { display: 'none' } : undefined}>
      {children}
    </div>
  </div>
);
